import { useState } from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { ChangeFeed, type CompetitorChangeItem } from "./ChangeFeed"
import type { PageProps } from "@/types/inertia"

export type CompetitorItem = {
  id: number
  name: string
  url: string
  host: string | null
  brand: "gisela" | "sanctuary" | null
  notes: string | null
  active: boolean
  last_checked_at: string | null
  last_status: string | null
  page_count: number
  recent_changes_count: number
}

type Props = PageProps & {
  competitors: CompetitorItem[]
  recent_changes: CompetitorChangeItem[]
}

function formatChecked(iso: string | null) {
  if (!iso) return "never"
  return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
}

function AddCompetitorForm() {
  const { props } = usePage<Props>()
  const errors = props.errors ?? {}
  const [name, setName] = useState("")
  const [url, setUrl] = useState("")
  const [brand, setBrand] = useState("")

  function submit(e: React.FormEvent) {
    e.preventDefault()
    router.post(
      "/admin/competitors",
      { competitor: { name, url, brand: brand || null } },
      {
        onSuccess: () => {
          setName("")
          setUrl("")
          setBrand("")
        },
      },
    )
  }

  return (
    <form onSubmit={submit} className="mt-6 rounded-lg border border-hairline bg-surface p-5">
      <h2 className="mb-4">Add a website to watch</h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <label htmlFor="competitor-name">Name</label>
          <Input
            id="competitor-name"
            className="mt-1"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Calm Minds Therapy"
            required
          />
          {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
        </div>
        <div>
          <label htmlFor="competitor-url">Website URL</label>
          <Input
            id="competitor-url"
            className="mt-1"
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.co.uk"
            required
          />
          {errors.url && <p className="mt-1 text-sm text-red-500">{errors.url}</p>}
        </div>
        <div>
          <label htmlFor="competitor-brand">Competes with</label>
          <Select
            id="competitor-brand"
            className="mt-1"
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
          >
            <option value="">Both / general</option>
            <option value="gisela">Gisela Ramirez (therapy)</option>
            <option value="sanctuary">Sanctuary (room hire)</option>
          </Select>
        </div>
        <div className="flex items-end">
          <Button type="submit">Add &amp; scan</Button>
        </div>
      </div>
    </form>
  )
}

export default function CompetitorsIndex() {
  const { props } = usePage<Props>()
  const { competitors, recent_changes } = props
  const flash = props.flash

  return (
    <AdminShell>
      <Head title="Competitor Watch">
        <meta name="description" content="Track competitor websites for new pages, new sections, and content changes." />
        <meta property="og:title" content="Competitor Watch" />
        <meta property="og:description" content="Track competitor websites for new pages, new sections, and content changes." />
      </Head>

      <PageHeader
        title="Competitor Watch"
        description="Similar websites, scanned nightly for new pages, new sections, and rewritten copy — so you can spot what they add and respond on your own site."
      />

      {flash?.notice && <p className="mt-4 text-sm text-accent">{flash.notice}</p>}

      <p className="mt-4 max-w-3xl text-sm text-ink-muted">
        Their visitor numbers are private, so traffic can't be compared directly — for traffic
        estimates and keyword gaps use a tool like Ahrefs. What this tracks is public: content,
        page structure, and site size over time.
      </p>

      <AddCompetitorForm />

      <div className="mt-6 overflow-x-auto rounded-lg border border-hairline">
        <table className="w-full text-sm">
          <thead className="border-b border-hairline bg-surface">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-ink-muted">Website</th>
              <th className="px-4 py-3 text-left font-medium text-ink-muted">Competes with</th>
              <th className="px-4 py-3 text-right font-medium text-ink-muted">Pages</th>
              <th className="px-4 py-3 text-right font-medium text-ink-muted">Changes (7d)</th>
              <th className="hidden px-4 py-3 text-left font-medium text-ink-muted lg:table-cell">Last checked</th>
              <th className="px-4 py-3 text-right font-medium text-ink-muted">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-hairline">
            {competitors.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-ink-muted">
                  No websites watched yet — add one above.
                </td>
              </tr>
            )}
            {competitors.map((c) => (
              <tr key={c.id} className="transition-colors hover:bg-surface/30">
                <td className="px-4 py-3">
                  <Link href={`/admin/competitors/${c.id}`} className="font-medium no-underline">
                    {c.name}
                  </Link>
                  <p className="font-mono text-xs text-ink-muted">{c.host}</p>
                </td>
                <td className="px-4 py-3">
                  {c.brand === "gisela" ? (
                    <Badge tone="accent">Gisela</Badge>
                  ) : c.brand === "sanctuary" ? (
                    <Badge tone="accent">Sanctuary</Badge>
                  ) : (
                    <Badge tone="neutral">Both</Badge>
                  )}
                </td>
                <td className="px-4 py-3 text-right tabular-nums">{c.page_count}</td>
                <td className="px-4 py-3 text-right tabular-nums">
                  {c.recent_changes_count > 0 ? (
                    <span className="font-medium text-accent">{c.recent_changes_count}</span>
                  ) : (
                    <span className="text-ink-muted">0</span>
                  )}
                </td>
                <td className="hidden px-4 py-3 text-ink-muted lg:table-cell">
                  {formatChecked(c.last_checked_at)}
                  {c.last_status === "error" && (
                    <Badge tone="signal" className="ml-2">error</Badge>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    <Button size="sm" variant="secondary" onClick={() => router.post(`/admin/competitors/${c.id}/check`)}>
                      Check now
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        if (confirm(`Stop watching ${c.name}? Its history will be deleted.`)) {
                          router.delete(`/admin/competitors/${c.id}`)
                        }
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-8 rounded-lg border border-hairline bg-surface">
        <div className="border-b border-hairline px-5 py-3">
          <h2>Latest changes across all watched sites</h2>
        </div>
        <ChangeFeed changes={recent_changes} emptyText="Nothing detected yet — changes appear here after scans run." />
      </div>
    </AdminShell>
  )
}
